// "Edit flight details" modal of a ShareJet booking: status and flight ID.
import { useEffect } from 'react';
import { App as AntdApp, Form, Input, Modal, Select } from 'antd';
import { LIMITS } from '../../data/limits';

import { BOOKING_STATUSES, type BookingStatus } from '../../api/types';
import { BOOKING_STATUS_LABELS, settableBookingStatus } from '../../data/statuses';
import type { AdminBookingDetail } from '../../api/types';
import { extractError, type SubmitFn } from './bookingUpdate';
import { useSingleSubmit } from '../../hooks/useSingleSubmit';

export function FlightDetailsModal({
  open,
  onClose,
  booking,
  onSubmit,
  submitting,
}: {
  open: boolean;
  onClose: () => void;
  booking: AdminBookingDetail;
  onSubmit: SubmitFn;
  submitting: boolean;
}) {
  const { message, modal } = AntdApp.useApp();
  const [form] = Form.useForm<{ status: BookingStatus; flightNumber: string }>();
  useEffect(() => {
    if (open)
      form.setFieldsValue({
        status: booking.status,
        flightNumber: booking.flightNumber ?? '',
      });
  }, [open, booking, form]);

  const doSubmit = async (v: { status: BookingStatus; flightNumber: string }) => {
    try {
      await onSubmit({ status: settableBookingStatus(v.status), flightNumber: v.flightNumber?.trim() ?? '' });
      message.success('Flight details saved');
      onClose();
    } catch (e) {
      message.error(extractError(e));
    }
  };

  // Regression 28 Sep — one submit at a time.
  const submitGuard = useSingleSubmit();
  const handleOk = submitGuard.wrap(async () => {
    const v = await form.validateFields();
    // Confirming a booking CAPTURES/CHARGES the held pre-authorisation — double-check.
    if (v.status === 'confirmed' && booking.status !== 'confirmed') {
      modal.confirm({
        title: 'Confirm & charge',
        content:
          'Confirming this booking will CAPTURE/CHARGE the held pre-authorisation on the customer’s card. Continue?',
        okText: 'Confirm & charge',
        okButtonProps: { danger: true },
        onOk: () => doSubmit(v),
      });
      return;
    }
    // SHAR-485 — per SHAR-29, ANY status change must be confirmed via a pop-up
    // (the confirm was lost in the modal refactor, only the confirmed/charge case
    // kept it). Skip the confirm only when the status is unchanged.
    // SHAR-591 (retest) — cancelling is terminal, so its confirmation says so
    // rather than reading like any other status change. The admin has to be told
    // BEFORE the click that there is no route back, because afterwards the
    // status field is locked and there is nothing to discover the rule from.
    if (v.status === 'cancelled' && booking.status !== 'cancelled') {
      modal.confirm({
        title: 'Cancel this booking?',
        content:
          'Cancelling is IRREVERSIBLE. Once this booking is cancelled its status becomes final and ' +
          'cannot be changed back to any other status, by you or by anyone else.' +
          // SHAR-1090 — a confirmed booking has been charged, and cancelling it
          // does not give the money back: the admin has to be told before the click.
          (booking.status === 'confirmed'
            ? ' Captured payments are NOT refunded automatically: refund them manually via the payment provider.'
            : '') +
          ' Continue?',
        okText: 'Cancel booking',
        okButtonProps: { danger: true },
        cancelText: 'Keep booking',
        onOk: () => doSubmit(v),
      });
      return;
    }
    if (v.status !== booking.status) {
      modal.confirm({
        title: 'Apply status change',
        content: `Change status to “${BOOKING_STATUS_LABELS[v.status as keyof typeof BOOKING_STATUS_LABELS]}”?`,
        okText: 'Apply',
        onOk: () => doSubmit(v),
      });
      return;
    }
    await doSubmit(v);
  });

  return (
    <Modal
      title="Edit flight details"
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      confirmLoading={submitting || submitGuard.busy}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Required' }]}>
          <Select<BookingStatus>
            // SHAR-413 — 'complete' is a computed status, not settable by admin.
            // SHAR-1202 — and only the moves the API allows from the current status.
            options={BOOKING_STATUSES.filter(
              (s) =>
                s !== 'complete' &&
                (!booking.allowedStatusChanges || s === booking.status || booking.allowedStatusChanges.includes(s)),
            ).map((s) => ({
              value: s,
              label: BOOKING_STATUS_LABELS[s],
            }))}
          />
        </Form.Item>
        <Form.Item name="flightNumber" label="Flight ID">
          <Input placeholder="e.g. SJ123" maxLength={LIMITS.flightNumber} allowClear />
        </Form.Item>
      </Form>
    </Modal>
  );
}
